import { useMemo, useCallback } from 'react';
import { authService } from '../utils/auth';
import { useSession } from '../context/SessionContext';

/**
 * VALID_BUSINESS_ROLES — Authoritative list of enterprise business roles.
 * Must stay in sync with rbac_engine.py VALID_BUSINESS_ROLES.
 */
export const VALID_BUSINESS_ROLES = ["owner", "admin", "sales", "finance", "legal", "member", "viewer", "procurement", "operations", "workforce"];

/**
 * Ordinary Revenue permissions eligible for temporary Solution Access bypass.
 * Approval permissions (commit.approve, proposal.approve) are strictly excluded.
 */
export const REVENUE_NORMAL_PERMISSIONS = [
  "revenue.view",
  "revenue.manage",
  "evaluation.view",
  "evaluation.edit",
  "commit.view",
  "commit.edit",
  "proposal.view",
  "proposal.create",
  "proposal.edit",
  "proposal.send",
  "handoff.view",
  "handoff.manage",
];

/**
 * Bi-directional capability alias map between frontend keys and backend hierarchical catalog.
 */
export const PERMISSION_ALIASES = {
  // Legacy UI checks -> backend hierarchical permissions
  "revenue.view": ["revenue.leads.read", "revenue.view", "leads.view"],
  "revenue.manage": ["revenue.leads.update", "revenue.leads.create", "revenue.manage", "leads.edit"],
  "leads.view": ["revenue.leads.read", "leads.view", "revenue.view"],
  "leads.create": ["revenue.leads.create", "leads.create"],
  "leads.edit": ["revenue.leads.update", "leads.edit", "revenue.manage"],
  "leads.delete": ["revenue.leads.delete", "leads.delete"],
  "leads.assign": ["revenue.leads.assign", "leads.assign"],
  "leads.export": ["revenue.leads.export", "leads.export"],
  "leads.import": ["revenue.leads.import", "leads.import"],
  "evaluation.view": ["revenue.evaluations.read", "evaluation.view"],
  "evaluation.edit": ["revenue.evaluations.update", "evaluation.edit"],
  "evaluation.approve": ["revenue.evaluations.update", "evaluation.approve"],
  "commit.view": ["revenue.commits.read", "commit.view"],
  "commit.edit": ["revenue.commits.create", "commit.edit"],
  "commit.approve": ["revenue.commits.approve", "commit.approve"],
  "proposal.view": ["revenue.proposals.read", "proposal.view"],
  "proposal.create": ["revenue.proposals.create", "proposal.create"],
  "proposal.edit": ["revenue.proposals.update", "proposal.edit"],
  "proposal.send": ["revenue.proposals.send", "proposal.send"],
  "proposal.approve": ["revenue.proposals.approve", "proposal.approve"],
  "proposal.delete": ["revenue.proposals.delete", "proposal.delete"],
  "contract.view": ["revenue.contracts.read", "contract.view"],
  "contract.create": ["revenue.contracts.create", "contract.create"],
  "contract.edit": ["revenue.contracts.update", "contract.edit"],
  "contract.send": ["revenue.contracts.send", "contract.send"],
  "contract.approve": ["revenue.contracts.approve", "contract.approve"],
  "contract.sign": ["revenue.contracts.sign", "contract.sign"],
  "contract.delete": ["revenue.contracts.delete", "contract.delete"],
  "handoff.view": ["revenue.handoffs.read", "handoff.view"],
  "handoff.manage": ["revenue.handoffs.manage", "handoff.manage"],
  "parties.view": ["revenue.parties.read", "parties.view"],
  "parties.manage": ["revenue.parties.manage", "parties.manage"],
  "governance.view": ["revenue.governance.read", "governance.view"],
  "governance.manage": ["revenue.governance.manage", "governance.manage"],
  "offerings.view": ["revenue.offerings.read", "offerings.view"],
  "offerings.manage": ["revenue.offerings.manage", "offerings.manage"],
  "revenue.offerings.read": ["revenue.offerings.read", "offerings.view"],
  "revenue.offerings.manage": ["revenue.offerings.manage", "offerings.manage"],
  "revenue.engagements.read": ["revenue.engagements.read", "engagements.view"],
  "revenue.engagements.create": ["revenue.engagements.create", "engagements.create"],
  // Administration, Users, Roles & Settings permissions
  "users.view": ["admin.users.read", "admin.users.manage", "users.view"],
  "users.manage": ["admin.users.manage", "users.manage"],
  "admin.users.read": ["admin.users.read"],
  "admin.users.manage": ["admin.users.manage"],
  "admin.users.invite": ["admin.users.invite", "admin.users.manage"],
  "roles.view": ["admin.roles.read", "admin.roles.manage", "roles.view"],
  "roles.manage": ["admin.roles.manage", "roles.manage"],
  "admin.roles.read": ["admin.roles.read"],
  "admin.roles.manage": ["admin.roles.manage"],
  "admin.roles.create": ["admin.roles.create", "admin.roles.manage"],
  "settings.view": ["admin.settings.read", "admin.settings.manage", "settings.view"],
  "settings.manage": ["admin.settings.manage", "settings.manage"],
  "admin.settings.read": ["admin.settings.read"],
  "admin.settings.manage": ["admin.settings.manage"],
};

/**
 * Permission mappings per business role.
 * Mirrors backend rbac_engine.py DEFAULT_ROLE_PERMISSIONS exactly.
 * Note: owner is an auth-layer role, NOT a business role.
 */
const ROLE_PERMISSIONS = {
  owner: ["*"],
  admin: ["*"],
  sales: [
    "revenue.view", "revenue.manage",
    "evaluation.view", "evaluation.edit",
    "commit.view", "commit.edit",
    "proposal.view", "proposal.create", "proposal.edit", "proposal.send",
    "contract.view", "handoff.view",
    "users.view"
  ],
  finance: [
    "revenue.view",
    "commit.view", "commit.edit", "commit.approve",
    "proposal.view", "proposal.approve",
    "contract.view", "contract.create", "contract.edit", "contract.send", "contract.approve",
    "handoff.view",
    "users.view"
  ],
  legal: [
    "revenue.view",
    "contract.view", "contract.create", "contract.edit", "contract.send", "contract.approve", "contract.sign",
    "handoff.view", "handoff.manage",
    "users.view"
  ],
  member: ["users.view"],
  viewer: ["users.view"],
  procurement: ["users.view"],
  operations: ["users.view"],
  workforce: [
    "workforce.view", "workforce.manage",
    "workforce.organization.view", "workforce.organization.manage",
    "workforce.department.view", "workforce.department.manage",
    "workforce.location.view", "workforce.location.manage",
    "workforce.position.view", "workforce.position.manage",
    "workforce.employee.view", "workforce.employee.create", "workforce.employee.edit", "workforce.employee.manage", "workforce.employee.delete",
    "workforce.attendance.view", "workforce.attendance.manage",
    "workforce.leave.view", "workforce.leave.approve", "workforce.leave.manage",
    "workforce.payroll.view", "workforce.payroll.run", "workforce.payroll.manage",
    "workforce.reports.view",
    "users.view"
  ]
};

/**
 * Centralized check to determine if a user is an organization administrator
 * (owner or admin).
 */
export const hasOrgAdminAccess = (user) => {
  if (!user) return false;
  if (user.is_owner === true || user.is_admin === true) return true;
  const role = String(user.orgRole || user.org_role || user.role_id || user.role || "").toLowerCase().trim();
  return ["owner", "admin"].includes(role);
};

/**
 * Centralized check to determine if a user can archive a specific Revenue Lead.
 * Permitted for:
 * 1. Organization Owner / Admin
 * 2. Currently assigned Lead Owner (lead.owner_id == user.user_id / user.id)
 */
export const canArchiveRevenueLead = (user, lead) => {
  if (!user || !lead) return false;
  const role = String(user.orgRole || user.org_role || user.role_id || user.role || "").toLowerCase().trim();
  if (["owner", "admin"].includes(role)) return true;

  const currentUid = String(user.user_id || user.id || user._id || "").trim();
  const leadOwnerId = String(lead.owner_id || lead.assigned_to || "").trim();

  if (leadOwnerId && currentUid && leadOwnerId === currentUid) {
    return true;
  }
  return false;
};

/**
 * Centralized check to determine if a user can unarchive a specific Revenue Lead.
 */
export const canUnarchiveRevenueLead = (user, lead) => {
  return canArchiveRevenueLead(user, lead);
};

/**
 * Centralized check to determine if a user can delete a specific Revenue Lead.
 * Permitted for:
 * 1. Organization Owner / Admin
 * 2. Currently assigned Lead Owner (lead.owner_id == user.user_id / user.id)
 */
export const canDeleteRevenueLead = (user, lead) => {
  if (!user || !lead) return false;
  const role = String(user.orgRole || user.org_role || user.role_id || user.role || "").toLowerCase().trim();
  if (["owner", "admin"].includes(role)) return true;

  const currentUid = String(user.user_id || user.id || user._id || "").trim();
  const leadOwnerId = String(lead.owner_id || lead.assigned_to || "").trim();

  if (leadOwnerId && currentUid && leadOwnerId === currentUid) {
    return true;
  }
  return false;
};

/**
 * Centralized check to determine if a user can restore a soft-deleted Revenue Lead.
 * Permitted for:
 * 1. Organization Owner / Admin
 * (Assigned Lead Owner alone is NOT permitted)
 * (Expired leads past permanent_delete_at are NOT restorable)
 */
export const canRestoreRevenueLead = (user, lead = null) => {
  if (!user) return false;
  
  const role = String(user.orgRole || user.org_role || user.role_id || user.role || "").toLowerCase().trim();
  const hasAdminRole = ["owner", "admin"].includes(role);
  if (!hasAdminRole) return false;

  if (lead && lead.permanent_delete_at) {
    try {
      const expTime = new Date(lead.permanent_delete_at).getTime();
      if (!Number.isNaN(expTime) && Date.now() >= expTime) {
        return false;
      }
    } catch (e) {
      // ignore
    }
  }

  return true;
};

export const usePermissions = () => {
  // Prefer the reactive session so a live Solution Access change re-renders consumers.
  // Falls back to cached local identity if session user is still resolving.
  const session = useSession();
  const user = (session && session.user) ? session.user : (authService.getUser() || null);

  // Resolve org-specific role (org_users.role) — never fall back to a privileged role.
  const orgRole = user ? (user.orgRole || user.org_role || user.role_id || user.role || null) : null;

  // Normalize: only accept recognized business roles; everything else is null.
  const role = VALID_BUSINESS_ROLES.includes(orgRole) ? orgRole : null;
  const roleName = user ? (user.role_name || user.role || orgRole || "Member") : "";

  const isSuperAdmin = false;
  const isOwner = Boolean(
    user?.is_owner ||
    (typeof orgRole === "string" && orgRole.toLowerCase() === "owner")
  );
  const isAdmin = Boolean(
    user?.is_admin ||
    (typeof orgRole === "string" && orgRole.toLowerCase() === "admin")
  );
  const isOrgAdmin = isOwner || isAdmin || hasOrgAdminAccess(user);
  const isManager = Boolean(user?.is_manager);

  // Authoritative permissions: prioritize database effective permissions sent from backend
  const permissions = useMemo(() => {
    if (Array.isArray(user?.permissions) && user.permissions.length > 0) {
      return user.permissions;
    }
    if (Array.isArray(user?.effective_permissions) && user.effective_permissions.length > 0) {
      return user.effective_permissions;
    }
    if (role && ROLE_PERMISSIONS[role]) {
      return ROLE_PERMISSIONS[role];
    }
    return [];
  }, [user?.permissions, user?.effective_permissions, role]);

  // ── Solution Access ──────────────────────────────────────────────────────
  // Mirrors backend routes/deps.py::has_solution. This is UX only — the backend gate is
  // the enforcement boundary. Never trust this for anything but rendering.
  const solutions = useMemo(() => user?.solutions ?? [], [user?.solutions]);
  // Solution Access is only known once GET /api/auth/me has landed. The login/signup
  // responses persist a user payload with no `solutions` key, so between navigate() and
  // the session refresh the answer is genuinely UNKNOWN — which must not be treated as
  // "denied", or the route guard bounces a valid user to /no-access. Render optimistically
  // while unknown; the backend gate is the enforcement boundary either way.
  const solutionsLoaded = Array.isArray(user?.solutions);
  const hasSolution = useCallback(
    (solutionKey) => {
      if (!solutionKey) return true; // an item that declares no solution is always shown

      // Level 1: Platform Organization Entitlement
      // If organization has explicitly disabled this solution, deny immediately (even for Owner).
      const orgSolutions = user?.organization?.solutions_enabled;
      if (orgSolutions && typeof orgSolutions === "object" && orgSolutions[solutionKey] === false) {
        return false;
      }

      // Level 2: User Solution Access
      const rawRole = (user?.orgRole || user?.role_id || user?.role || "").toLowerCase();
      if (rawRole === "owner") return true;
      if (!solutionsLoaded) return true; // unknown yet — see `solutionsLoaded` above
      return solutions.includes(solutionKey);
    },
    [solutions, solutionsLoaded, user]
  );

  const hasPermission = useCallback(
    (permission) => {
      if (!permission) return true;
      if (isOrgAdmin) return true;
      if (!permissions || permissions.length === 0) return false;
      if (permissions.includes("*")) return true;
      if (permissions.includes(permission)) return true;

      const aliases = PERMISSION_ALIASES[permission];
      if (aliases && aliases.some((a) => permissions.includes(a))) {
        return true;
      }
      return false;
    },
    [permissions, isOrgAdmin]
  );

  const hasAnyPermission = useCallback(
    (requiredPermissions) => {
      if (!requiredPermissions || requiredPermissions.length === 0) return true;
      if (isOrgAdmin) return true;
      if (!permissions || permissions.length === 0) return false;
      if (permissions.includes("*")) return true;
      return requiredPermissions.some((p) => hasPermission(p));
    },
    [permissions, isOrgAdmin, hasPermission]
  );

  const canAccessSettings = useMemo(() => {
    if (!user) return false;

    // 1. Authoritative backend resolution takes primary precedence
    if (typeof user?.can_access_settings === "boolean") {
      return user.can_access_settings;
    }

    // 2. Org Admin has access
    if (isOrgAdmin) return true;

    // 3. Hierarchical managers with child roles have delegated Settings access
    if (user?.is_manager) return true;

    // 4. Granular Settings permissions (excluding ordinary member users.view)
    return (
      hasPermission("admin.settings.read") ||
      hasPermission("admin.settings.manage") ||
      hasPermission("admin.users.read") ||
      hasPermission("admin.users.manage") ||
      hasPermission("admin.users.invite") ||
      hasPermission("admin.roles.read") ||
      hasPermission("admin.roles.manage") ||
      hasPermission("admin.roles.create") ||
      hasPermission("settings.view") ||
      hasPermission("settings.manage") ||
      hasPermission("users.manage") ||
      hasPermission("roles.view") ||
      hasPermission("roles.manage")
    );
  }, [user, user?.can_access_settings, user?.is_manager, isOrgAdmin, hasPermission]);

  // Separate navigation visibility conditions for Sidebar Account Menu:
  // 1. Owner & Admin: See 'Organization Admin', hide separate 'Settings'
  // 2. Authorized subordinate roles (Team Lead, Manager, Custom) & authorized members:
  //    Hide 'Organization Admin', show 'Settings' only if possessing Settings capabilities
  // 3. Unauthorized managers & ordinary members: Hide both
  const canViewOrgAdminNav = Boolean(user && isOrgAdmin);
  const canViewSettingsNav = Boolean(user && !isOrgAdmin && canAccessSettings);

  return {
    user,
    loading: session?.loading ?? false,
    isRevalidating: session?.isRevalidating ?? false,
    permissions,
    hasPermission,
    hasAnyPermission,
    role,
    roleName,
    isSuperAdmin,
    isOwner,
    isAdmin,
    isOrgAdmin,
    isManager,
    canAccessSettings,
    canViewOrgAdminNav,
    canViewSettingsNav,
    solutions,
    solutionsLegacy: false,
    solutionsLoaded,
    hasSolution,
    userId: user?.user_id || user?.id || user?._id,
  };
};
